import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import { athleteDisplayName } from '@strength/domain';
import { MdAdd, MdCancel, MdPlaylistAdd } from '@teamworksdev/icons';
import { RouterOutput } from '../../../../../trpc';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAthletesQuery } from '../../hooks/useAthletesQuery';
import { useIsZeroSurfaceActive } from '../../../../zero/zero-active';
import { ZeroAthletePicker } from './ZeroAthletePicker';
import type { AthleteTeamProfile } from '@strength/domain';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/form/form';
import { AthleteAvatar } from '../../../../components/icons/athlete-avatar';
import { DebouncedSearchInput } from '../../../../components/input/debounced-search-input';
import { Input } from '../../../../components/input/input';
import {
  DeleteConfirmationModal,
  deleteTrackText,
} from '../../../../components/modals/delete-confirmation-modal';
import { Tabs, TabTrigger } from '../../../../components/tab/tab';
import { cn } from '../../../../components/utils';
import { TrackAthletePreview, TrackForEdit, TrackPreview } from '../types';
import {
  AthleteChangeConfirmationData,
  ChangeAthleteConfirmationModal,
} from './ChangeConfirmationModal';
import { ColorSwatchPicker } from './ColorSwatchPicker';

export const trackForm = z.object({
  name: z.string().min(1).max(40),
  athleteIds: z.array(z.string()),
  color: z.string().nullable().optional(),
});

export type TrackForm = z.infer<typeof trackForm>;

export type TrackAthlete = TrackAthletePreview;
export type TeamAthletes = RouterOutput['roster']['teamAthletes'][0];
type TrackSubmitData = Pick<TrackForm, 'name' | 'athleteIds' | 'color'>;

export function getTrackFormDefaults(
  defaultValues: TrackPreview | undefined,
  parentTrack: TrackPreview | undefined,
): TrackForm {
  if (defaultValues) {
    return {
      name: defaultValues.name,
      athleteIds: defaultValues.athletes.map((a) => a.profileId),
      color: defaultValues.color ?? null,
    };
  }
  return {
    name: '',
    athleteIds: [],
    color: parentTrack?.color ?? null,
  };
}

export function detectAthleteChanges(
  newAthleteIds: string[],
  currentAthletes: readonly TrackAthlete[] | undefined,
  availableAthletes: TeamAthletes[] | undefined,
): AthleteChangeConfirmationData {
  if (!currentAthletes || !availableAthletes) {
    return { show: false, added: [], removed: [] };
  }

  const addedAthletes = newAthleteIds
    .filter((id) => !currentAthletes.some((a) => a.profileId === id))
    .map((id) => availableAthletes.find((a) => a.id === id))
    .filter((a) => !!a);

  const removedAthletes = currentAthletes
    .filter((a) => !newAthleteIds.includes(a.profileId))
    .map((a) => availableAthletes.find((athlete) => athlete.id === a.profileId))
    .filter((a) => !!a);

  return {
    show: addedAthletes.length > 0 || removedAthletes.length > 0,
    added: addedAthletes,
    removed: removedAthletes,
  };
}

export function TrackForm({
  onSave,
  onClose,
  open,
  teamId,
  defaultValues,
  track,
  onDelete,
}: {
  onSave: (
    name: string,
    athleteIds: string[],
    color: string | null,
    parentId?: string,
  ) => void;
  onClose: () => void;
  open: boolean;
  teamId: string;
  track?: TrackForEdit;
  defaultValues?: TrackPreview;
  onDelete?: (trackId: string) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // The picker scopes its Zero query to the track's own team — the same team
  // scope the tRPC header gives the flag-off path. The viewer's other teams
  // must not surface their athletes here.
  const pickerTeamIds = useMemo(() => [teamId], [teamId]);
  // Zero-active renders the athlete picker through the zero-virtual
  // virtualizer (SC-5674); flag off keeps the plain tRPC-fed list.
  const isZeroSurfaceActive = useIsZeroSurfaceActive('tracks');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<
    'all' | 'selected' | 'unselected'
  >('all');
  const [confirmationData, setConfirmationData] =
    useState<AthleteChangeConfirmationData>({
      show: false,
      added: [],
      removed: [],
    });
  const isEdit = !!defaultValues;
  const hasPublishedBlocks = isEdit && !onDelete;
  const form = useForm<TrackForm>({
    resolver: zodResolver(trackForm),
    defaultValues: getTrackFormDefaults(defaultValues, track?.parentTrack),
  });

  const isChangedAthlete = useCallback(
    (athleteId: string, change: 'added' | 'removed') => {
      if (!isEdit) return false;
      if (change === 'added')
        return !defaultValues?.athletes?.some((a) => a.profileId === athleteId);
      return defaultValues?.athletes?.some((a) => a.profileId === athleteId);
    },
    [isEdit, defaultValues?.athletes],
  );

  // Flag off: the tRPC route filters server-side by searchTerm and, for
  // sub-tracks, by the parent track's roster — the pre-Zero behavior,
  // byte-identical. Flag on: the list is one synced read, so the same
  // narrowing happens client-side (plus in the virtualizer's query args —
  // SC-5674).
  const athletesQuery = useAthletesQuery({
    input: isZeroSurfaceActive
      ? undefined
      : {
          searchTerm: debouncedSearchTerm,
          parentTrackId: track?.parentTrack?.id,
        },
  });
  const parentAthleteIds = useMemo(
    () => new Set((track?.parentTrack?.athletes ?? []).map((a) => a.profileId)),
    [track?.parentTrack],
  );
  const athletesForTeam = useMemo(() => {
    if (!isZeroSurfaceActive) return athletesQuery.data ?? [];
    let list = athletesQuery.data ?? [];
    if (track?.parentTrack?.id) {
      list = list.filter((a) => parentAthleteIds.has(a.id));
    }
    // Field-wise search, matching the tRPC route's predicate (given OR
    // family name contains) — NOT the shared display-name helper, whose
    // combined-name match the drawer wants but tRPC does not do.
    const term = debouncedSearchTerm.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      ({ givenName, familyName }) =>
        givenName.toLowerCase().includes(term) ||
        familyName.toLowerCase().includes(term),
    );
  }, [
    isZeroSurfaceActive,
    athletesQuery.data,
    track?.parentTrack?.id,
    parentAthleteIds,
    debouncedSearchTerm,
  ]);
  const athleteQueryStatus = athletesQuery.status;
  // While the Zero roster query is pending or has failed, `memberships ?? []`
  // reads as an empty roster: Select All would clear the selection and
  // change detection would drop real adds (suppressing the confirmation
  // modal), so Select All and the save path stay gated until the roster has
  // synced once. Flag-off (tRPC) keeps today's behavior byte-identical.
  const isRosterGated = isZeroSurfaceActive && athleteQueryStatus !== 'success';

  const { handleSubmit, reset, formState } = form;

  const onConfirmAthleteChangeSubmit = ({
    name,
    athleteIds,
    color,
  }: TrackSubmitData) => {
    onSave(name, athleteIds, color ?? null, track?.parentTrack?.id);
    setConfirmationData({ show: false, added: [], removed: [] });
    reset();
  };

  const onFormSubmit = ({ name, athleteIds, color }: TrackSubmitData) => {
    // The save path is gated with Select All until the Zero roster query has
    // synced (covers the Enter-key submit, which ignores a disabled button).
    if (isRosterGated) return;
    if (hasPublishedBlocks) {
      const changes = detectAthleteChanges(
        athleteIds,
        defaultValues?.athletes,
        athletesForTeam,
      );
      if (changes.show) {
        setConfirmationData(changes);
        return;
      }
    }
    onConfirmAthleteChangeSubmit({ name, athleteIds, color });
  };

  const onDeleteTrack = () => {
    if (onDelete && track?.id) {
      onDelete(track.id);
    }
    setDeleteModalOpen(false);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      anchor="right"
      sx={{
        '& .MuiPaper-root': {
          minWidth: 400,
          maxHeight: '100vh',
        },
      }}
    >
      <DrawerHeader
        title={
          isEdit
            ? track?.parentTrack
              ? t('editSubTrack')
              : t('editTrack')
            : track?.parentTrack
              ? t('addSubTrack')
              : t('addTrack')
        }
        onClose={onClose}
      />
      <Form {...form}>
        <form
          id="track-form"
          onSubmit={handleSubmit(onFormSubmit)}
          className="flex flex-col h-full overflow-hidden"
        >
          <div className="flex flex-col px-8 py-4 overflow-y-auto">
            <div className="flex gap-4 items-start">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1 space-y-1">
                    <FormLabel>{t('name')}</FormLabel>
                    <FormControl>
                      <Input
                        className={cn(
                          'h-9',
                          formState.errors.name && 'border-red-500',
                        )}
                        maxLength={40}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="color"
                render={({ field: { value, onChange } }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>{t('color')}</FormLabel>
                    <FormControl>
                      <ColorSwatchPicker
                        value={value ?? null}
                        onChange={onChange}
                        label={t('color')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="athleteIds"
              render={({ field: { value, onChange } }) => {
                const athleteList =
                  selectedTab === 'all'
                    ? athletesForTeam
                    : athletesForTeam?.filter((a) => {
                        return (
                          (selectedTab === 'selected' &&
                            value.includes(a.id)) ||
                          (selectedTab === 'unselected' &&
                            !value.includes(a.id))
                        );
                      });
                // Shared by the plain list (flag off) and the zero-virtual
                // picker (flag on) so the two renderings cannot drift.
                const renderAthleteRow = (
                  athlete: AthleteTeamProfile,
                  component?: React.ElementType,
                ) => {
                  const isSelected = value.includes(athlete.id);
                  let backgroundColor = 'white';
                  if (!isEdit) {
                    backgroundColor = isSelected ? grey[200] : 'white';
                  } else {
                    if (isSelected) {
                      backgroundColor = isChangedAthlete(athlete.id, 'added')
                        ? green[100]
                        : selectedTab !== 'selected'
                          ? grey[200]
                          : 'white';
                    } else if (isChangedAthlete(athlete.id, 'removed')) {
                      backgroundColor = red[100];
                    }
                  }
                  const toggleAthlete = () => {
                    if (value.includes(athlete.id)) {
                      onChange(value.filter((id) => id !== athlete.id));
                    } else {
                      onChange([...value, athlete.id]);
                    }
                  };
                  return (
                    <AthletePickerRow
                      key={athlete.id}
                      athlete={athlete}
                      isSelected={isSelected}
                      backgroundColor={backgroundColor}
                      onToggle={toggleAthlete}
                      removeLabel={t('removeAthlete', {
                        name: athleteDisplayName(athlete),
                      })}
                      addLabel={t('addAthlete', {
                        name: athleteDisplayName(athlete),
                      })}
                      iconColor={theme.palette.mediumEmphasisText}
                      component={component}
                    />
                  );
                };
                return (
                  <div className="flex flex-col flex-1 gap-2 mt-4 min-h-0">
                    <DebouncedSearchInput
                      label={t('assignUsers')}
                      placeholder={t('searchForAthlete')}
                      onDebouncedChange={setDebouncedSearchTerm}
                    />
                    <div>
                      <Tabs
                        value={selectedTab}
                        onChange={(_, newValue) => setSelectedTab(newValue)}
                        size="small"
                        type="buttonGroup"
                        aria-label={t('filterTabs')}
                      >
                        <TabTrigger label={t('all')} value="all" />
                        <TabTrigger label={t('selected')} value="selected" />
                        <TabTrigger
                          label={t('unselected')}
                          value="unselected"
                        />
                      </Tabs>
                    </div>

                    <span className="flex flex-col flex-1 min-h-0">
                      {isZeroSurfaceActive ? (
                        <ZeroAthletePicker
                          teamIds={pickerTeamIds}
                          searchTerm={debouncedSearchTerm}
                          parentTrackId={track?.parentTrack?.id}
                          athleteIds={selectedTab === 'all' ? undefined : value}
                          athleteIdsMode={
                            selectedTab === 'unselected' ? 'notIn' : 'in'
                          }
                          tabpanelId={`tabpanel-${selectedTab}`}
                          tabpanelLabelledBy={`tab-${selectedTab}`}
                          header={
                            <>
                              {athleteQueryStatus === 'loading' && (
                                <div>{t('loading')}</div>
                              )}
                              {athleteQueryStatus === 'error' && (
                                <div>{t('errorLoadingData')}</div>
                              )}
                              {selectedTab === 'all' && (
                                <>
                                  <div className="flex justify-end p-1">
                                    <Button
                                      size="small"
                                      variant="text"
                                      disabled={isRosterGated}
                                      onClick={() => {
                                        onChange(
                                          athleteList?.map(
                                            (athlete) => athlete.id,
                                          ) ?? [],
                                        );
                                      }}
                                      className="gap-2"
                                    >
                                      <MdPlaylistAdd
                                        aria-hidden="true"
                                        size={16}
                                      />
                                      {t('selectAll')}
                                    </Button>
                                  </div>
                                  <Divider className="mb-2" />
                                </>
                              )}
                            </>
                          }
                          renderAthlete={(athlete) =>
                            renderAthleteRow(athlete, 'div')
                          }
                        />
                      ) : (
                        <div
                          role="tabpanel"
                          id={`tabpanel-${selectedTab}`}
                          aria-labelledby={`tab-${selectedTab}`}
                          className={cn(
                            'overflow-y-auto border rounded flex-1 min-h-[200px]',
                          )}
                        >
                          {athleteQueryStatus === 'loading' && (
                            <div>{t('loading')}</div>
                          )}
                          {athleteQueryStatus === 'error' && (
                            <div>{t('errorLoadingData')}</div>
                          )}

                          {selectedTab === 'all' && (
                            <>
                              <div className="flex justify-end p-1">
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    onChange(
                                      athleteList?.map(
                                        (athlete) => athlete.id,
                                      ) ?? [],
                                    );
                                  }}
                                  className="gap-2"
                                >
                                  <MdPlaylistAdd aria-hidden="true" size={16} />
                                  {t('selectAll')}
                                </Button>
                              </div>
                              <Divider className="mb-2" />
                            </>
                          )}

                          <List
                            dense={true}
                            sx={{
                              padding: 0,
                              '& .MuiListItem-root': {
                                mb: 1,
                                '&:last-child': { mb: 0 },
                              },
                            }}
                          >
                            {athleteList?.map((athlete) =>
                              renderAthleteRow(athlete),
                            )}
                          </List>
                        </div>
                      )}
                      <FormMessage />
                      <p className="text-sm text-gray-500 mt-2">
                        {t('usersSelectedCount', {
                          count: form.getValues('athleteIds').length,
                        })}
                      </p>
                    </span>
                  </div>
                );
              }}
            />
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          isEdit ? 'justify-between' : 'justify-end',
          'flex flex-row gap-2 w-full p-8',
        )}
      >
        {isEdit && (
          <Button
            variant="containedDestructive"
            onClick={() => setDeleteModalOpen(true)}
            disabled={!onDelete}
          >
            {t('delete')}
          </Button>
        )}
        <span className="flex flex-row gap-2">
          <Button onClick={() => onClose()} variant="outlined">
            {t('cancel')}
          </Button>
          <Button type="submit" form="track-form" disabled={isRosterGated}>
            {t('save')}
          </Button>
        </span>
      </DrawerFooter>
      {track?.id && !!onDelete && (
        <DeleteConfirmationModal
          id={track.id}
          open={deleteModalOpen}
          {...deleteTrackText(t, !!track?.parentTrack?.id)}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => onDeleteTrack()}
        />
      )}
      <ChangeAthleteConfirmationModal
        id={defaultValues?.id ?? ''}
        name={defaultValues?.name ?? ''}
        confirmationData={confirmationData}
        onConfirm={handleSubmit(onConfirmAthleteChangeSubmit)}
        onCancel={() =>
          setConfirmationData({ show: false, added: [], removed: [] })
        }
      />
    </Drawer>
  );
}

/**
 * One picker row, shared by the plain list (flag off) and the zero-virtual
 * picker (flag on) so the two renderings cannot drift. `component` lets the
 * virtualized path render valid div-in-div markup inside its row wrappers.
 */
// SAFETY: MUI v6's ListItem overloads reject the `component` prop when
// `secondaryAction` is set, though the runtime accepts it. The picker renders
// 'div' rows only inside the zero-virtual wrappers (valid div-in-div markup);
// the plain path passes undefined and gets the default `li`.
const ListItemAny = ListItem as unknown as React.ComponentType<{
  component?: React.ElementType;
  disablePadding?: boolean;
  secondaryAction?: React.ReactNode;
  sx?: Record<string, unknown>;
  children?: React.ReactNode;
}>;

function AthletePickerRow({
  athlete,
  isSelected,
  backgroundColor,
  onToggle,
  removeLabel,
  addLabel,
  iconColor,
  component,
}: {
  athlete: AthleteTeamProfile;
  isSelected: boolean;
  backgroundColor: string;
  onToggle: () => void;
  removeLabel: string;
  addLabel: string;
  iconColor: string;
  component?: React.ElementType;
}) {
  return (
    <ListItemAny
      component={component}
      disablePadding
      secondaryAction={
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={isSelected ? removeLabel : addLabel}
        >
          {isSelected ? (
            <MdCancel aria-hidden="true" size={20} color={iconColor} />
          ) : (
            <MdAdd aria-hidden="true" size={20} color={iconColor} />
          )}
        </IconButton>
      }
      sx={{
        backgroundColor,
      }}
    >
      <ListItemButton
        onClick={onToggle}
        sx={{
          '&:hover': {
            backgroundColor: backgroundColor === 'white' ? grey[50] : 'default',
          },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 0, paddingRight: '8px' }}>
          <AthleteAvatar
            athlete={athlete}
            sx={{
              width: 24,
              height: 24,
              fontSize: 12,
            }}
            imgSrc={athlete.imageUrl ?? ''}
          />
        </ListItemAvatar>
        <ListItemText
          primary={athleteDisplayName(athlete)}
          secondary={
            athlete.attributes?.positions
              ? athlete.attributes.positions.join(', ')
              : undefined
          }
        />
      </ListItemButton>
    </ListItemAny>
  );
}
